import { DashboardEventService } from './../dashboard/dashboard-event.service';
import { AppConfig } from './../app.config';
import * as moment from 'moment';

class Command {
    name: string;
    parameters: string[] = [];
}

class CommandContext {
    constructor(public appConfig: AppConfig, public dashboardEventService: DashboardEventService) {
    }
}

interface CommandHandler {
    helpText: string;
    minParamCount: number;
    maxParamCount: number;
    handleCommand(command: Command, context: CommandContext): string;
}

const COMMAND_HANDLERS: { [key: string]: CommandHandler } = {
    'HELP': {
        handleCommand(command: Command, context: CommandContext) {
            if (command.parameters.length === 0) {
                return `Supported commands: ${Object.keys(COMMAND_HANDLERS).join(', ')}`;
            } else {
                const handler = COMMAND_HANDLERS[command.parameters[0].toUpperCase()];
                if (!handler) {
                    throw new Error(`Command ${command.parameters[0]} found`);
                }
                return `Usage: ${command.parameters[0]} <${handler.minParamCount} - ${handler.maxParamCount} parameters>.
                ${handler.helpText}`;
            }
        },
        minParamCount: 0,
        maxParamCount: 1
    } as CommandHandler,
    'DATE': {
        handleCommand(command: Command, context: CommandContext) {
            return new Date().toDateString();
        },
        minParamCount: 0,
        maxParamCount: 0
    } as CommandHandler,
    'OPERATING-DATE': {
        handleCommand(command: Command, context: CommandContext) {
            if (command.parameters.length === 0) {
                return context.appConfig.getTodayString();
            } else {
                const m = moment(command.parameters[0]);
                const date = m.toDate();
                context.dashboardEventService.restartSubject.next(date);
            }
        },
        minParamCount: 0,
        maxParamCount: 1
    } as CommandHandler,
}

export class DebugTerminalHandler {
    constructor(private appConfig: AppConfig, private dashboardEventService: DashboardEventService) {

    }

    handleTerminalCommand(command: string): string {
        try {
            const commands = this.parseCommandInput(command);
            commands
                .forEach((cmd) => {
                    const handler = COMMAND_HANDLERS[cmd.name];
                    if (cmd.parameters.length < handler.minParamCount) {
                        throw new Error(`Command expected at least ${handler.minParamCount} parameters`);
                    }
                    if (cmd.parameters.length > handler.maxParamCount) {
                        throw new Error(`Command expected at at most ${handler.maxParamCount} parameters`);
                    }
                })
            const context = new CommandContext(this.appConfig, this.dashboardEventService);
            return commands
                .map((cmd) => {
                    return COMMAND_HANDLERS[cmd.name].handleCommand(cmd, context);
                })
                .join('\n');
        } catch (err) {
            return err;
        }
    }

    parseCommandInput(commandInput: string): Command[] {
        const commands = [];
        const splits = commandInput.split(' ');
        const commandNames = Object.keys(COMMAND_HANDLERS);
        return this.parseCommandSplits(splits, commandNames, commands);
    }

    parseCommandSplits(splits: string[], commandNames: string[], commands: Command[]) {
        let currentCommand: Command;
        for (let i = 0; i < splits.length; i++) {
            const split = splits[i].trim();
            if (split === ';') {
                commands.push(...this.parseCommandSplits(splits.slice(i + 1), commandNames, commands));
                break;
            }
            if (!currentCommand) {
                // No command so let's assume we are receiving a new command
                if (!commandNames.includes(split.toUpperCase())) {
                    throw new Error(`Unknown command: ${split}`);
                }
                currentCommand = new Command();
                currentCommand.name = split.toUpperCase();
                commands.push(currentCommand);
            } else {
                const handler = COMMAND_HANDLERS[currentCommand.name];
                if (!handler) {
                    throw new Error(`No handler found for command: ${currentCommand.name}`);
                }
                currentCommand.parameters.push(split);
            }
        }
        return commands;
    }
}
